import { useCallback, useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiFileText, FiRefreshCw, FiSearch } from "react-icons/fi";
import AdminVerificationDetail, {
  APPLICANT_LABEL,
  KycStatusSelect,
  sendKybDecision,
} from "../components/AdminVerificationDetail";
import { getAdminBusinesses } from "../api/admin";
import { adminTheme } from "../styles/adminTheme";

/**
 * Every business and its verification (KYB). A business fills in the form when it's
 * created — an individual's or a company's, whichever its owner said it's registered
 * to — and waits as pending; here it's approved or declined, and the owner is notified
 * either way. Businesses from before KYB existed show as not submitted.
 *
 * For now the status blocks nothing — it's a record, not a gate.
 *
 * @see mintly-back/src/controllers/adminKycController
 */

const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

const FILTERS = [
  { id: "", label: "All businesses" },
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "declined", label: "Declined" },
  { id: "none", label: "Not submitted" },
];

const AdminBusinessesTab = ({ isDark, setToast, baseText, mutedText, cardBase }) => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ q: "", kyb: "" });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  /** The business being changed, so only its row is disabled. */
  const [busy, setBusy] = useState(null);
  /** Whose form is open. */
  const [open, setOpen] = useState(null);

  const theme = adminTheme(isDark);
  const { inputBase, rowBorder } = theme;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminBusinesses({ ...filters, page, limit: 50 });
      setRows(res.data || []);
      setCounts(res.counts || {});
      setPages(res.pages || 1);
      setTotal(res.total || 0);
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not load businesses");
    } finally {
      setLoading(false);
    }
  }, [filters, page, setToast]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (row, status) => {
    setBusy(row.id);
    try {
      const res = await sendKybDecision(row.id, status, row.name);
      if (!res) return;
      setToast?.(
        `${row.name} → ${status}${res.changed && status !== "pending" ? " · owner notified" : ""}`
      );
      await load();
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not change the status");
    } finally {
      setBusy(null);
    }
  };

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setFilters((prev) => ({ ...prev, q: search.trim() }));
  };

  return (
    <div className="space-y-6">
      <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
          <div>
            <h3 className={`text-base font-semibold tracking-tight ${baseText}`}>Businesses ({total})</h3>
            <p className={`text-xs ${mutedText} mt-1 max-w-2xl`}>
              Every business fills in the KYB form when it's created — as an individual or as a
              company — and starts as pending. Approving or declining notifies the owner. The
              status doesn't block anything yet.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className={`${theme.btnSecondary}`}
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <form onSubmit={applySearch} className="relative flex-1 min-w-[240px]">
            <FiSearch className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${mutedText}`} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Business name, owner username or email"
              className={`w-full rounded-xl border pl-9 pr-3 py-2 text-sm outline-none ${inputBase}`}
            />
          </form>
          <select
            value={filters.kyb}
            onChange={(e) => {
              setPage(1);
              setFilters((prev) => ({ ...prev, kyb: e.target.value }));
            }}
            aria-label="KYB status"
            className={`rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
          >
            {FILTERS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
                {f.id && counts[f.id] !== undefined ? ` (${counts[f.id]})` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className={theme.tableWrap}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left ${theme.thead}`}>
                {["Business", "Owner", "Type", "Created", "KYB status", ""].map((h) => (
                  <th key={h} className={theme.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className={`border-t ${rowBorder} ${theme.rowHover} align-top`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {b.logo ? (
                        <img src={b.logo} alt="" className="h-8 w-8 shrink-0 rounded-lg object-cover" />
                      ) : (
                        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-mint/20 text-xs font-bold text-mint">
                          {(b.name || "?")[0].toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className={`font-semibold ${baseText}`}>{b.name || "—"}</div>
                        {b.kyb.legalName && b.kyb.legalName !== b.name && (
                          <div className={`text-[11px] ${mutedText}`}>{b.kyb.legalName}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className={baseText}>{b.owner?.username || "—"}</div>
                    <div className={`text-[11px] ${mutedText}`}>{b.owner?.email}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {b.kyb.applicantType ? (
                      <span
                        className={`inline-flex items-center rounded-lg px-2 py-0.5 text-[11px] font-semibold ${
                          b.kyb.applicantType === "individual"
                            ? "bg-violet-500/10 text-violet-400"
                            : "bg-aqua/10 text-aqua"
                        }`}
                      >
                        {APPLICANT_LABEL[b.kyb.applicantType]}
                      </span>
                    ) : (
                      <span className={`text-xs ${mutedText}`}>—</span>
                    )}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap ${mutedText}`}>{when(b.createdAt)}</td>
                  <td className="px-4 py-3">
                    <KycStatusSelect
                      status={b.kyb.status}
                      disabled={busy === b.id}
                      onChange={(status) => changeStatus(b, status)}
                      isDark={isDark}
                      mutedText={mutedText}
                      label={`${b.name} KYB status`}
                    />
                    {b.kyb.status === "declined" && b.kyb.decisionReason && (
                      <div className="mt-1 max-w-[220px] text-[11px] text-rose-400">{b.kyb.decisionReason}</div>
                    )}
                    {b.kyb.submittedAt && (
                      <div className={`mt-1 text-[11px] ${mutedText}`}>submitted {when(b.kyb.submittedAt)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setOpen(b.id)}
                      className={`flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${rowBorder} ${baseText} hover:text-mint`}
                    >
                      <FiFileText className="h-3.5 w-3.5" />
                      View form
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className={`py-10 text-center ${mutedText}`}>
                    No businesses match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4">
          <span className={`text-xs ${mutedText}`}>
            Page {page} of {pages}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className={`${theme.iconBtn} disabled:opacity-40`}
              aria-label="Previous page"
            >
              <FiChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className={`${theme.iconBtn} disabled:opacity-40`}
              aria-label="Next page"
            >
              <FiChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {open && (
        <AdminVerificationDetail
          id={open}
          isDark={isDark}
          setToast={setToast}
          onClose={() => setOpen(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default AdminBusinessesTab;
